import { appConfig } from "@/constants";
import { trackLegacyEvent } from "@analytics";
import { Box, ConfirmationDialog, Stack } from "@biom3/react";
import { useState } from "react";
import { ImageWithOverlay } from "./ImageWithOverlay";
import {
  type Archetype,
  type Option,
  QUIZ_QUESTIONS_A,
  QUIZ_QUESTIONS_B,
  QUIZ_QUESTIONS_C,
  resultOptionsA,
  resultOptionsB,
  resultOptionsC,
} from "./quizQuestions";

type QuizQuestModalProps = {
  onClose: () => void;
  onComplete: () => void;
  quizVariant: "archetype" | "profession" | "test";
  gameUid: string;
};

export const QuizQuestModal = ({
  onClose,
  onComplete,
  quizVariant,
  gameUid,
}: QuizQuestModalProps) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [showResults, setShowResults] = useState(false);
  const [result, setResult] = useState<Archetype | null>(null);
  const [copied, setCopied] = useState(false);
  const [incorrectAnswer, setIncorrectAnswer] = useState<number | null>(null);

  // Get questions and results based on variant
  const questions = (() => {
    switch (quizVariant) {
      case "archetype":
        return QUIZ_QUESTIONS_A;
      case "profession":
        return QUIZ_QUESTIONS_B;
      case "test":
        return QUIZ_QUESTIONS_C;
      default:
        return [];
    }
  })();

  const resultOptions = (() => {
    switch (quizVariant) {
      case "archetype":
        return resultOptionsA;
      case "profession":
        return resultOptionsB;
      case "test":
        return resultOptionsC;
    }
  })();

  // Handles when a user selects an answer option for the current question
  const handleOptionSelect = (option: Option) => {
    trackLegacyEvent({
      screen: "QuizQuest",
      userJourney: "Games",
      action: "Pressed",
      control: option.text,
      controlType: "Button",
      extras: {
        variant: quizVariant,
        gameUid,
      },
    });

    const newScores = { ...scores };

    for (const { category, points } of option.scores || []) {
      newScores[category] = (newScores[category] || 0) + points;
    }
    setScores(newScores);

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      const result = Object.entries(resultOptions).find(
        ([category]) =>
          newScores[category] === Math.max(...Object.values(newScores)),
      )?.[1];

      if (!result) return;

      setResult(result);
      setShowResults(true);
    }
  };

  const handleClose = () => {
    setCurrentQuestionIndex(0);
    setScores({});
    setShowResults(false);
    setResult(null);
    onClose();
  };

  const completeQuiz = () => {
    if (showResults) {
      trackLegacyEvent({
        screen: "QuizQuest",
        userJourney: "Games",
        action: "Pressed",
        control: "Complete Quest",
        controlType: "Button",
        extras: {
          variant: quizVariant,
          gameUid,
        },
      });
      onComplete();
      onClose();
    }
  };

  const handleShareableText = (result: Archetype | null): void => {
    if (!result) return;

    const text = generateShareableText(result);

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    });

    trackLegacyEvent({
      screen: "QuizQuest",
      userJourney: "Games",
      action: "Pressed",
      control: "Copy link",
      controlType: "Button",
      extras: {
        variant: quizVariant,
        gameUid,
      },
    });
  };

  const generateShareableText = (result: Archetype | null): string => {
    if (!result) return "";

    const quizSourceUrl = `${appConfig.ASSET_BASE_URL}/games/ravenquest`;
    const prefix = `I got the ${result.title} ${quizVariant} in RavenQuest!`;
    const suffix = `Find out your ${quizVariant}: ${quizSourceUrl}`;

    if (quizVariant === "test") {
      return result.summary;
    }
    return `${prefix} ${result.summary} ${suffix}`;
  };

  const getShareUrl = (result: Archetype | null): string => {
    if (!result) return "";
    const text = generateShareableText(result);
    return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
  };

  const currentQuestion = questions[currentQuestionIndex];

  const handleRetakeQuiz = () => {
    setCurrentQuestionIndex(0);
    setScores({});
    setShowResults(false);
    setResult(null);

    trackLegacyEvent({
      screen: "QuizQuest",
      userJourney: "Games",
      action: "Pressed",
      control: "Retake Quiz",
      controlType: "Button",
      extras: {
        variant: quizVariant,
        gameUid,
      },
    });
  };

  const handleShareOnX = (result: Archetype | null) => {
    if (!result) return;
    window.open(getShareUrl(result), "_blank");
    trackLegacyEvent({
      screen: "QuizQuest",
      userJourney: "Games",
      action: "Pressed",
      control: "Share on X",
      controlType: "Button",
      extras: {
        variant: quizVariant,
        gameUid,
      },
    });
  };

  const handleOptionClick = (optionIndex: number) => {
    const currentQuestion = questions[currentQuestionIndex];
    const selectedOption = currentQuestion.options[optionIndex];

    if (quizVariant === "test") {
      if (isCorrectAnswer(selectedOption)) {
        setIncorrectAnswer(null);
        handleOptionSelect(selectedOption);
      } else {
        setIncorrectAnswer(optionIndex);
        setTimeout(() => setIncorrectAnswer(null), 500);
      }
    } else {
      handleOptionSelect(selectedOption);
    }
  };

  const isCorrectAnswer = (option: Option): boolean => {
    if (quizVariant === "test") {
      return !!option.scores?.some((score) => score.points > 0);
    }
    return false;
  };

  return (
    <ConfirmationDialog outsideClicksClose visible>
      <ConfirmationDialog.Content
        sx={{
          bgc: "base.color.neutral.900",
          maxHeight: "720px",
          minHeight: "640px",
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        <ConfirmationDialog.Content.CloseButtCon
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: "base.spacing.x4",
            left: "base.spacing.x4",
          }}
        />
        {!showResults ? (
          <>
            {quizVariant !== "profession" && (
              <ConfirmationDialog.Content.Image
                use={
                  <img
                    src={currentQuestion.image}
                    alt="Character"
                    width="430"
                    height="220"
                  />
                }
              />
            )}
            <ConfirmationDialog.Content.Title
              sx={{
                pt: quizVariant !== "profession" ? "0" : "base.spacing.x18",
                pb: "base.spacing.x2",
              }}
            >
              {currentQuestion.text}
            </ConfirmationDialog.Content.Title>
            <Stack direction="column" gap="base.spacing.x3">
              {currentQuestion.options.map((option, index) => (
                <ConfirmationDialog.Content.Button
                  key={`${currentQuestionIndex}-${option.text}`}
                  onClick={() => handleOptionClick(index)}
                  size="medium"
                  variant="tertiary"
                  sx={{
                    "& > span": {
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    },
                    ...(incorrectAnswer === index && {
                      animation:
                        "shake 0.6s cubic-bezier(.36,.07,.19,.97) both",
                      "@keyframes shake": {
                        "0%, 100%": { transform: "translateX(0)" },
                        "10%, 30%, 50%, 70%, 90%": {
                          transform: "translateX(-15px)",
                        },
                        "20%, 40%, 60%, 80%": { transform: "translateX(15px)" },
                      },
                    }),
                  }}
                >
                  {option.text}
                </ConfirmationDialog.Content.Button>
              ))}
            </Stack>
          </>
        ) : (
          <>
            {quizVariant !== "profession" ? (
              <ConfirmationDialog.Content.Image
                use={
                  <ImageWithOverlay
                    src="/images/quests/lens-flare-background.png"
                    alt="Character"
                    overlaySrc={result?.img}
                    width="850"
                    height="100%"
                    mt="auto"
                    overlayWidth="auto"
                    overlayHeight={quizVariant === "test" ? "90%" : "40%"}
                    overlayTop="50%"
                    overlayLeft="50%"
                    overlayTransform="translate(-50%, -50%)"
                  />
                }
              />
            ) : (
              <ConfirmationDialog.Content.Image
                use={
                  <img
                    src={result ? result.img : ""}
                    alt="Character"
                    width="430"
                    height="211"
                  />
                }
              />
            )}
            <Stack
              sx={{ mt: quizVariant !== "profession" ? "-40px" : "-25px" }}
            >
              <ConfirmationDialog.Content.Title>
                {result?.title}
              </ConfirmationDialog.Content.Title>
              {result?.caption && (
                <ConfirmationDialog.Content.Caption>
                  <b>{result?.caption}</b>
                </ConfirmationDialog.Content.Caption>
              )}
              <ConfirmationDialog.Content.Caption>
                {result?.description}
              </ConfirmationDialog.Content.Caption>
              <Box
                sx={{
                  display: "flex",
                  gap: "base.spacing.x2",
                  justifyContent: "center",
                  mt: "base.spacing.x2",
                }}
              >
                <ConfirmationDialog.Content.Button
                  size="small"
                  variant="secondary"
                  onClick={() => handleShareOnX(result)}
                >
                  <ConfirmationDialog.Content.Button.Icon
                    icon="X"
                    iconVariant="bold"
                  />
                  Share on X
                </ConfirmationDialog.Content.Button>
                <ConfirmationDialog.Content.Button
                  size="small"
                  variant="secondary"
                  onClick={() => handleShareableText(result)}
                >
                  <ConfirmationDialog.Content.Button.Icon
                    icon={copied ? "Tick" : "WalletConnect"}
                  />
                  {copied ? "Copied!" : "Copy link"}
                </ConfirmationDialog.Content.Button>
              </Box>
            </Stack>

            <ConfirmationDialog.Content.Button
              size="large"
              variant="primary"
              onClick={completeQuiz}
            >
              Complete Quiz
            </ConfirmationDialog.Content.Button>
            <ConfirmationDialog.Content.Button
              size="large"
              variant="secondary"
              onClick={handleRetakeQuiz}
            >
              Retake Quiz
            </ConfirmationDialog.Content.Button>
          </>
        )}
      </ConfirmationDialog.Content>
    </ConfirmationDialog>
  );
};
